import { useQuery } from "@tanstack/react-query";
import { fetchLobby } from "../lib/api";
import { usePrivateKey } from "./usePrivateKey";

/**
 * The `["lobby"]` query — `GET /api/v1/lobby`, the app's **flags payload**
 * (A121 Item 3), and the one place `shop_enabled` reaches the client.
 *
 * Exported so that every reader of the flag is provably the same cache entry
 * rather than three hand-typed string arrays that happen to match today:
 * `useShopEnabled` below (the four chrome entry points), `useShopFlag`
 * (`/shop`'s own branch) and `useLobby` (the lobby screen's first paint) all
 * observe this one key, so one fetch answers all of them.
 */
/**
 * The lobby read's key **tail**, shared by every observer of `GET /lobby`.
 *
 * **Private** (SEC-2, T-910): the route needs no session, but its answer
 * depends on one — `viewer.role` and `viewer.balanceCoins` are the caller's
 * (api `routes/lobby.ts` `resolveViewer`) — so it is keyed through
 * `usePrivateKey` like every other read whose response the cookie decides.
 */
export const LOBBY_QUERY_KEY = ["lobby"] as const;

/**
 * **Is the shop open?** — the single home for Ivy's four SHOP-ENTRY-POINTS
 * surfaces (`design/wr-shop-storefront-ruling.md` addendum-d §1, LOCKED):
 * the drawer's Shop row, the top-bar coin chip's `live` state, the locked
 * lobby tile's `Get coins` button and the room post-game bar's.
 *
 * > *"all four read the same `shopEnabled` the storefront reads
 * > (`lobby.meta.shopEnabled`, the existing flags payload). One field, one
 * > home, no per-surface copy (A81), so the four cannot disagree and they
 * > flip together."*
 *
 * ── Three deliberate properties ────────────────────────────────────────────
 *
 * 1. **Unknown is closed.** The flag is `true` only once a lobby payload has
 *    actually arrived saying so; loading, error and "never fetched" all read
 *    `false`. That is not a fallback, it is §4.3's LOCKED default — *"while
 *    `shop_enabled === false`, zero entry points render"* — and it is the only
 *    direction that is safe to be wrong in: a missing door costs a typed URL,
 *    a phantom one costs a dead end.
 * 2. **No latch, unlike `useShopFlag`.** §6.1's true→false asymmetry (*"the
 *    storefront holds until the next mount"*) is a rule about a **mounted
 *    storefront** — it exists so a flag-off flip cannot tear down an open
 *    dialog under a reading player. Chrome has nothing to tear down, and
 *    addendum-d's *"they flip together"* is the governing sentence here, so an
 *    entry point disappears the moment the flag says it should.
 * 3. **No poll, and no second copy of the flag.** Ivy addendum-d item 2 is
 *    explicit that rule 25's ~1s *"is not a licence to mint a one-second poll
 *    from every idle client for a state that changes about twice in the
 *    product's life"*. Freshness here comes from the query the app already
 *    runs: the lobby screen's own observer (`staleTime: 0`, so every arrival
 *    at `/lobby` refetches), this observer mounting on a cold `/room` or
 *    `/profile` load, and `/shop`'s focus/`visibilitychange` refetches. The
 *    consequence, stated so it is not discovered: a player who sits on one
 *    screen through a flip sees chrome change on their next screen change,
 *    not within ~1s. See the T-440 Output's watch-items.
 */
export function useShopEnabled(): boolean {
  const query = useQuery({ queryKey: usePrivateKey(LOBBY_QUERY_KEY), queryFn: fetchLobby });
  return query.data?.meta.shopEnabled === true;
}
